import React from "react";
import * as Sentry from "@sentry/react";

// Transient DOM-reconciliation faults. These happen when the live DOM is mutated
// outside of React's knowledge — most commonly by browser translation extensions
// (e.g. Google Translate rewriting text nodes, which is very likely on non-English
// forms) or by contentEditable widgets like draft-js. React's fiber then points at
// a node that is no longer where it expects, and a commit throws NotFoundError on
// insertBefore/removeChild. Fully remounting the affected subtree rebuilds the DOM
// from scratch and clears the desync, so we auto-recover before ever showing the
// scary error screen.
const MAX_AUTO_RETRIES = 2;

function isTransientDomError(error) {
	if (!error) return false;
	if (error.name === "NotFoundError") return true;
	const msg = String(error.message || "");
	return (
		msg.indexOf("insertBefore") !== -1 ||
		msg.indexOf("removeChild") !== -1 ||
		msg.indexOf("is not a child of this node") !== -1
	);
}

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null, errorInfo: null, retryKey: 0, retries: 0 };
		this.handleReset = this.handleReset.bind(this);
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error, errorInfo) {
		// Report every caught error so the underlying trigger stays visible even when
		// we auto-recover (Sentry is a no-op outside production/staging).
		try {
			Sentry.captureException(error, { extra: { componentStack: errorInfo && errorInfo.componentStack } });
		} catch (e) {
			// ignore reporting failures
		}
		console.log(error, errorInfo);

		// Silently rebuild the subtree for transient DOM desyncs before surfacing UI.
		if (isTransientDomError(error) && this.state.retries < MAX_AUTO_RETRIES) {
			this.setState((prev) => ({
				error: null,
				errorInfo: null,
				retryKey: prev.retryKey + 1,
				retries: prev.retries + 1
			}));
			return;
		}

		this.setState({ error, errorInfo });
	}

	handleReset() {
		this.setState((prev) => ({
			error: null,
			errorInfo: null,
			retryKey: prev.retryKey + 1,
			retries: 0
		}));
	}

	render() {
		if (this.state.error || this.state.errorInfo) {
			if (this.props.compact) {
				// Field/section-level: keep the rest of the document usable.
				return (
					<div
						style={{
							border: "1px solid #e0a0a0",
							borderRadius: "4px",
							padding: "0.75em 1em",
							margin: "0.5em 0",
							background: "#fff6f6",
							color: "#000",
							fontSize: "0.85em"
						}}
					>
						<span>This section couldn&apos;t be displayed. </span>
						<button
							onClick={this.handleReset}
							style={{ border: "none", background: "transparent", color: "#0645ad", cursor: "pointer", padding: 0 }}
						>
							Try again
						</button>
						{typeof window !== "undefined" && window.google && window.google.translate ? (
							<span> — turning off page translation may help.</span>
						) : null}
					</div>
				);
			}

			// Error path
			return (
				<div style={{ border: "2px solid red", padding: "1em", marginBottom: "1em", background: "#fff", color: "#000" }}>
					<h2 style={{ fontWeight: "bold" }}>Something went wrong</h2>
					<p>
						Please share a screenshot of the entire page, URL, and steps to recreate in the{" "}
						<a href="https://slack.com/app_redirect?channel=feedback" target="_blank" rel="noreferrer">
							#feedback
						</a>{" "}
						Slack channel
					</p>
					<p>
						If this page is being translated by your browser, turning translation off and reloading usually fixes it.
					</p>
					<button onClick={this.handleReset} style={{ margin: "0.5em 0", padding: "0.4em 0.8em", cursor: "pointer" }}>
						Try again
					</button>
					<div style={{ whiteSpace: "pre-wrap", fontSize: "0.8em", margin: "1em 0" }}>
						{this.state.error && this.state.error.toString()}
						<br />
						{this.state.errorInfo && this.state.errorInfo.componentStack}
					</div>
				</div>
			);
		}

		// Normally, just render children. The key lets a reset/auto-retry fully
		// remount the subtree and rebuild the DOM.
		return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
	}
}

export default ErrorBoundary;
